import { useEffect, useMemo, useState } from "react";
import axios from "../../../api/axios";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DataTable from "examples/Tables/DataTable";

export default function TaxDeductionTypes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    code: "",
    name: "",
    source: "staff_claim",
    requires_evidence: false,
    is_active: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get("/tax/deduction-types", { params: { per_page: 200 } });
      setRows(res.data?.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.code || !form.name) return alert("Code and Name are required");
    await axios.post("/tax/deduction-types", form);
    setForm({
      code: "",
      name: "",
      source: "staff_claim",
      requires_evidence: false,
      is_active: true,
    });
    await load();
  };

  const update = async (id, patch) => {
    await axios.put(`/tax/deduction-types/${id}`, patch);
    await load();
  };

  const deactivate = async (id) => {
    if (!window.confirm("Deactivate this type?")) return;
    await axios.delete(`/tax/deduction-types/${id}`);
    await load();
  };

  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      { Header: "Code", accessor: "code" },
      { Header: "Name", accessor: "name" },
      { Header: "Source", accessor: "source" },
      {
        Header: "Requires Evidence",
        accessor: "requires_evidence",
        Cell: (cell) => (cell?.value ? "Yes" : "No"),
      },
      {
        Header: "Active",
        accessor: "is_active",
        disableSortBy: true,
        Cell: (cell) => {
          const r = cell?.row?.original || {};
          const active = !!r?.is_active;

          return (
            <MDButton
              size="small"
              color={active ? "success" : "dark"}
              disabled={!r?.id || loading}
              onClick={() => update(r.id, { is_active: !active })}
            >
              {active ? "Active" : "Inactive"}
            </MDButton>
          );
        },
      },
      {
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: (cell) => {
          const r = cell?.row?.original || {};

          return (
            <MDBox display="flex" gap={1} flexWrap="wrap">
              <MDButton
                size="small"
                color="info"
                disabled={!r?.id || loading}
                onClick={() => {
                  const name = window.prompt("Update name:", r.name || "");
                  if (name && name.trim()) update(r.id, { name: name.trim() });
                }}
              >
                Rename
              </MDButton>

              <MDButton
                size="small"
                color="secondary"
                disabled={!r?.id || loading}
                onClick={() => update(r.id, { requires_evidence: !r?.requires_evidence })}
              >
                Toggle Evidence
              </MDButton>

              <MDButton
                size="small"
                color="error"
                disabled={!r?.id || loading}
                onClick={() => deactivate(r.id)}
              >
                Deactivate
              </MDButton>
            </MDBox>
          );
        },
      },
    ],
    // include functions + loading so the buttons always use latest state
    // [loading]
    [loading, update, deactivate]
  );

  return (
    <MDBox p={3}>
      <MDTypography variant="h5">Tax Deduction Types</MDTypography>
      <MDTypography variant="button" color="text">
        Configure what staff can claim (and whether evidence is required).
      </MDTypography>

      <MDBox mt={3} p={2} sx={{ border: "1px solid #eee", borderRadius: 2 }}>
        <MDTypography variant="h6">Create Type</MDTypography>

        <MDBox mt={1} display="flex" gap={2} flexWrap="wrap" alignItems="center">
          <MDBox width={160}>
            <MDInput
              label="Code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
            />
          </MDBox>

          <MDBox width={260}>
            <MDInput
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </MDBox>

          <MDBox display="flex" gap={1} alignItems="center">
            <MDButton
              size="small"
              color={form.source === "staff_claim" ? "info" : "dark"}
              onClick={() => setForm({ ...form, source: "staff_claim" })}
            >
              staff_claim
            </MDButton>
            <MDButton
              size="small"
              color={form.source === "payroll_auto" ? "info" : "dark"}
              onClick={() => setForm({ ...form, source: "payroll_auto" })}
            >
              payroll_auto
            </MDButton>
          </MDBox>

          <MDButton
            size="small"
            color={form.requires_evidence ? "warning" : "dark"}
            onClick={() => setForm({ ...form, requires_evidence: !form.requires_evidence })}
          >
            Evidence: {form.requires_evidence ? "Yes" : "No"}
          </MDButton>

          <MDButton
            size="small"
            color={form.is_active ? "success" : "dark"}
            onClick={() => setForm({ ...form, is_active: !form.is_active })}
          >
            {form.is_active ? "Active" : "Inactive"}
          </MDButton>

          <MDButton color="info" onClick={create} disabled={loading}>
            Save
          </MDButton>

          <MDButton color="secondary" onClick={load} disabled={loading}>
            Refresh
          </MDButton>
        </MDBox>
      </MDBox>

      <MDBox mt={3}>
        <DataTable
          table={{ columns, rows }}
          entriesPerPage={false}
          showTotalEntries={false}
          canSearch
          pagination
          noEndBorder
        />
      </MDBox>
    </MDBox>
  );
}
